import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import { computed, onMounted, reactive, ref, toRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import {
  useGetCustomerNameRecommendationForInvoiceSync,
  useGetSyncOptionsQuery,
  useInvoiceQuery,
  useRequestBatchSync,
  useUpdateInvoiceSyncCustomerNameMutation,
} from '@/api/invoice'
import { useStore } from 'vuex-composition-helpers'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsText from '@/components/vision/VsText.vue'
import { useInvoicesSyncStore } from '@/components/batches/invoicesSyncStore'
import { useBatchStates } from '@/components/batches/useBatchStates'
import {
  BatchSyncStatus,
  useBatchSyncStates,
} from '@/components/batches/useBatchSyncStates'
import VsButton from '@/components/vision/VsButton.vue'

interface FormData {
  customerName?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BatchSyncDetailsEditForm',
  props: {
    batchId: null,
    syncTargetId: null,
    parentRouteName: null,
    parentRouteParams: null
  },
  setup(__props: any) {

const props = __props as {
  batchId: string
  syncTargetId: string
  parentRouteName: string
  parentRouteParams: any
}



const batchIdRef = toRef(props, 'batchId')
const syncTargetIdRef = toRef(props, 'syncTargetId')
const parentRouteNameRef = toRef(props, 'parentRouteName')
const parentRouteParamsRef = toRef(props, 'parentRouteParams')
const batchSyncDetailsEditForm = ref()
const showSnackbarError = ref(false)
const errorMessage = ref(null)

const { batchHasLineItems, batchClientName } = useBatchStates(batchIdRef)
const { lastSyncRunLogLink, syncStatus, syncTarget } = useBatchSyncStates(
  batchIdRef,
  syncTargetIdRef
)
const formData = reactive<FormData>({
  customerName: undefined,
})
const router = useRouter()
const route = useRoute()
const store = useStore()
const invoicesSyncStore = useInvoicesSyncStore()
const isAdmin = computed<boolean | undefined>(
  () => store.getters['permissions/isAdmin']
)

const hasChanges = computed(() => {
  if (syncStatus.value === BatchSyncStatus.NoSyncTarget) return true
  if (route.query.initialCustomerName) {
    return formData.customerName !== route.query.initialCustomerName
  }
  return formData.customerName !== batchClientName.value
})

onMounted(() => {
  if (route.query.initialCustomerName) {
    formData.customerName = route.query.initialCustomerName as string
  }
})

watch(
  batchClientName,
  (newVal) => {
    if (newVal && !formData.customerName) {
      formData.customerName = newVal
    }
  },
  { immediate: true }
)

const { data: recommendations, isLoading: loadingRecommendations } =
  useGetCustomerNameRecommendationForInvoiceSync()
const formattedRecommendations = computed(() => {
  const formattedResults = recommendations.value
    ?.map((r) => ({
      text: r,
      value: r,
      description: '',
    }))
    .filter((r) => r.text !== batchClientName.value)
  const defaultValue = {
    text: batchClientName.value,
    value: batchClientName.value,
    description: 'Default',
  }
  return formattedResults
    ? [defaultValue].concat(formattedResults)
    : [defaultValue]
})

const { data: syncOptions } = useGetSyncOptionsQuery()
const {
  data: batch,
  isLoading: isBatchLoading,
  refetch: refetchBatch,
} = useInvoiceQuery(batchIdRef)
const showQBDBatchNumberLengthWarning = computed(
  () =>
    batch.value &&
    batch.value.invoiceNumber?.length > 11 &&
    syncOptions.value &&
    (syncOptions.value.find((s) => s.id === syncTargetIdRef.value)?.name ===
      'QuickBooks Desktop' ||
      syncTarget.value?.name === 'QuickBooks Desktop')
)

const setCustomerName = (input: string) => {
  formData.customerName = input
}

const saveLabel = computed(() =>
  syncStatus.value === BatchSyncStatus.NoSyncTarget ? 'Sync' : 'Save'
)

const { mutate: requestSync, isLoading: isRequestingSync } =
  useRequestBatchSync()
const {
  mutate: updateSyncCustomerName,
  isLoading: isUpdatingSyncCustomerName,
} = useUpdateInvoiceSyncCustomerNameMutation()

async function save() {
  if (!batchSyncDetailsEditForm.value.isValid()) {
    return
  }
  try {
    if (syncStatus.value === BatchSyncStatus.NoSyncTarget) {
      await requestSync({
        invoiceId: batchIdRef.value,
        targetId: syncTargetIdRef.value,
        customerName: formData.customerName ?? undefined,
      })
    } else {
      await updateSyncCustomerName({
        invoiceId: batchIdRef.value,
        targetId: syncTargetIdRef.value,
        customerName: formData.customerName ?? undefined,
      })
    }
    if (formData.customerName) {
      // TODO: Remove this call when we are 100% sure we don't want to autofill last entered customer name
      invoicesSyncStore.updateLastEnteredCustomerName(formData.customerName)
    }
    await refetchBatch()
    dismiss()
  } catch (error) {
    showSnackbarError.value = true
    errorMessage.value = error
  }
}
const dismiss = () => {
  router.push({
    name: parentRouteNameRef.value,
    params: parentRouteParamsRef.value,
  })
}

function clearSnackbarError() {
  showSnackbarError.value = false
}

const onLogClick = () => {
  if (lastSyncRunLogLink.value) {
    window.open(lastSyncRunLogLink.value)
  }
}

return { __sfc: true,props, batchIdRef, syncTargetIdRef, parentRouteNameRef, parentRouteParamsRef, batchSyncDetailsEditForm, showSnackbarError, errorMessage, batchHasLineItems, batchClientName, lastSyncRunLogLink, syncStatus, syncTarget, formData, router, route, store, invoicesSyncStore, isAdmin, hasChanges, recommendations, loadingRecommendations, formattedRecommendations, syncOptions, batch, isBatchLoading, refetchBatch, showQBDBatchNumberLengthWarning, setCustomerName, saveLabel, requestSync, isRequestingSync, updateSyncCustomerName, isUpdatingSyncCustomerName, save, dismiss, clearSnackbarError, onLogClick, FormLayout, VsForm, VsAutocomplete, VsSnackbar, VsText, BatchSyncStatus, VsButton }
}

})